/**
 * Shared form validation utility for both card and invoice payment paths.
 * Eliminates duplicate validation logic between different payment flows.
 */
export class BookingFormValidator {
    /**
     * Validates the event booking form using jQuery validation or HTML5 fallback.
     * Applied to both card and invoice payment methods.
     */
    static validateBookingForm(formId: string): boolean {
        const bookingForm = document.getElementById(formId) as HTMLFormElement | null;
        if (!bookingForm) {
            console.error(`[BookingFormValidator] Form with id ${formId} not found`);
            return false;
        }

        const addressValid = BookingFormValidator.validateCheckoutAddresses(bookingForm);

        const $: any = (window as any).$;
        const $form = $ ? $(bookingForm) : null;

        // Ensure validator exists
        if ($form && $?.validator?.unobtrusive?.parse) {
            if (!$form.data("validator") || !$form.data("unobtrusiveValidation")) {
                $.validator.unobtrusive.parse($form);
            }
        }

        // Validate using jQuery if available, fall back to HTML5
        if ($form && typeof $form.valid === "function") {
            const isValid = $form.valid();
            if (!isValid) {
                console.warn("[BookingFormValidator] Form validation failed");
            }
            return addressValid && isValid;
        } else {
            const isValid = bookingForm.checkValidity();
            if (!isValid) {
                bookingForm.reportValidity();
                console.warn("[BookingFormValidator] HTML5 form validation failed");
            }
            return addressValid && isValid;
        }
    }

    /**
     * Validates that each visible Loqate address lookup container has an address selected,
     * or that the manual entry form is shown instead. Runs independently of jQuery Validate
     * since jQuery skips hidden elements (display:none) by default.
     */
    static validateCheckoutAddresses(form: HTMLFormElement): boolean {
        let valid = true;

        form.querySelectorAll<HTMLElement>('.addresslookup-container').forEach(container => {
            const isBilling = (container.dataset.name || '') === 'Billing';

            // Skip billing address validation when SameAsDelivery is checked
            if (isBilling) {
                const billingDetails = form.querySelector<HTMLElement>('#billingDetails');
                if (!billingDetails || billingDetails.style.display === 'none') return;
            }

            const idPrefix = isBilling ? 'billing' : 'delivery';
            const manualContainer = document.getElementById(`${idPrefix}-manual-address`);
            const errorEl = container.querySelector<HTMLElement>('.js-address-lookup-validation-error');

            // Manual entry is active � jQuery handles the visible required fields
            if (manualContainer && manualContainer.offsetParent !== null) {
                if (errorEl) errorEl.style.display = 'none';
                return;
            }

            // Loqate mode is active � check that an address was selected
            const fieldName = isBilling ? 'BillingAddressLine1' : 'AddressLine1';
            const addressField = form.querySelector<HTMLInputElement>(`[name="${fieldName}"]`);

            if (!addressField?.value?.trim()) {
                if (errorEl) {
                    errorEl.textContent = 'Please search for and select an address, or enter your address manually.';
                    errorEl.style.display = 'block';
                }
                valid = false;
            } else {
                if (errorEl) errorEl.style.display = 'none';
            }
        });

        return valid;
    }

    /**
     * Sets up terms agreement validation that applies to both payment methods.
     */
    static setupTermsValidation(formId: string): void {
        const bookingForm = document.getElementById(formId) as HTMLFormElement | null;
        if (!bookingForm) return;

        const $: any = (window as any).$;
        const $form = $ ? $(bookingForm) : null;

        // Ensure validator exists first
        if ($form && $?.validator?.unobtrusive?.parse) {
            if (!$form.data("validator") || !$form.data("unobtrusiveValidation")) {
                $.validator.unobtrusive.parse($form);
            }
        }

        if (!$form || typeof $form.find !== "function") return;

        const $terms = $form.find("#TermsAllAgreed");
        if (!$terms.length) return;

        // Add/override the terms rule
        if (typeof $terms.rules === "function") {
            $terms.rules("remove");
            $terms.rules("add", {
                required: true,
                messages: { required: "Please agree to the terms and conditions." }
            });
        }
    }

    /**
     * Saves the form data to the session for both payment methods.
     * Shared between card and invoice flows.
     */
    static async saveFormToSession(
        formId: string,
        eventId: string,
        getFormDataFn: (form: HTMLFormElement) => any,
        saveEndpoint?: string
    ): Promise<boolean> {
        const form = document.getElementById(formId) as HTMLFormElement | null;
        if (!form) {
            console.error(`[BookingFormValidator] Cannot save form - form ${formId} not found`);
            return false;
        }

        try {
            const formData = JSON.stringify(getFormDataFn(form));
            const endpoint = saveEndpoint ?? `/umbraco/surface/StripeSurface/SaveEventBookingFormToSession?key=${eventId}`;

            const response = await fetch(
                endpoint,
                {
                    method: "POST",
                    body: formData,
                    headers: { "Content-Type": "application/json" },
                    cache: "no-cache"
                }
            );

            if (!response.ok) {
                console.error(`[BookingFormValidator] Failed to save form: HTTP ${response.status}`);
                return false;
            }

            return true;
        } catch (err) {
            console.error("[BookingFormValidator] Error saving form to session:", err);
            return false;
        }
    }
}
